type PaymentSummaryBase = {
  surchargePercentage?: number | null;
};

export type DisabledPaymentSummary = PaymentSummaryBase & {
  enabled: false;
};

export type QualpayPaymentSummary = PaymentSummaryBase & {
  enabled: true;
  provider: "Qualpay";
  merchantId: string;
  embeddedKey: string;
  javascriptUrl: string;
  cssUrl: string;
  environment: "test" | "prod";
};

export type DispatchProPaymentSummary = PaymentSummaryBase & {
  enabled: true;
  provider: "DispatchPro";
  merchantId: string;
  environment: "sandbox" | "live";
  applicationId: string;
};

export type DispatchProV2PaymentSummary = PaymentSummaryBase & {
  enabled: true;
  provider: "DispatchProV2";
  publicKey: string;
  gatewayUrl: string;
};

export type PaymentSummary =
  | DisabledPaymentSummary
  | QualpayPaymentSummary
  | DispatchProPaymentSummary
  | DispatchProV2PaymentSummary;

type TemporaryCardBase = {
  brand: string;
  type: "Credit" | "Debit" | "Prepaid";
  canSurcharge: boolean;
  expirationMonth: number;
  expirationYear: number;
  lastFour: string;
  nameOnCard: string;
  zipCode: string;
};

export type DispatchProTemporaryCard = TemporaryCardBase & {
  processor: "DispatchPro";
  id: string;
};

export type DispatchProV2TemporaryCard = TemporaryCardBase & {
  processor: "DispatchProV2";
  id: string;
};

export type TemporaryCard = DispatchProTemporaryCard | DispatchProV2TemporaryCard;

export type QualpayTokenizedCard = {
  processor: "Qualpay";
  cardToken: string;
  brand: string;
  lastFour: string;
  expirationMonth: number;
  expirationYear: number;
  nameOnCard: string;
  zipCode: string;
  canSurcharge: boolean;
};

export type OnlinePaymentRequest = {
  customerId: string;
  invoiceId?: string;
  amount: number;
  applySurcharge: boolean;
} & (
  | {
      qualpayCard: Omit<QualpayTokenizedCard, "processor" | "canSurcharge" | "lastFour"> & {
        last4: string;
      };
    }
  | {
      card: {
        id: string;
        token: string;
      };
    }
  | {
      dispatchProV2Card: {
        id: string;
      };
    }
);

export function isQualpayPayment(payment: PaymentSummary): payment is QualpayPaymentSummary {
  return payment.enabled && payment.provider === "Qualpay";
}

export function isDispatchProPayment(
  payment: PaymentSummary,
): payment is DispatchProPaymentSummary {
  return payment.enabled && payment.provider === "DispatchPro";
}

export function isDispatchProV2Payment(
  payment: PaymentSummary,
): payment is DispatchProV2PaymentSummary {
  return payment.enabled && payment.provider === "DispatchProV2";
}

export function canAttemptOnlinePayment(payment: PaymentSummary) {
  return payment.enabled;
}

export function getPaymentProviderLabel(payment: PaymentSummary) {
  if (isQualpayPayment(payment)) return "Qualpay";

  if (isDispatchProPayment(payment) || isDispatchProV2Payment(payment)) return "DispatchPro";

  return "payment provider";
}
